import { useEffect, useRef, useState } from 'react';
import InfoTip from './InfoTip';
import type {
  DataTypeClass,
  PropertySpec,
  AnomalyConfig,
  AnomalyType,
} from '../types/spec';

const DATA_TYPE_OPTIONS: { value: DataTypeClass; label: string }[] = [
  { value: 'numerical_continuous', label: 'Numerical (Continuous)' },
  { value: 'numerical_discrete', label: 'Numerical (Discrete)' },
  { value: 'categorical', label: 'Categorical' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'datetime', label: 'DateTime' },
  { value: 'text', label: 'Text' },
];

const ANOMALY_TYPE_OPTIONS: { value: AnomalyType; label: string }[] = [
  { value: 'spike', label: 'Spike' },
  { value: 'drift', label: 'Drift' },
  { value: 'stuck', label: 'Stuck' },
  { value: 'noise', label: 'Noise' },
];

const DEFAULT_ANOMALY: AnomalyConfig = {
  enabled: false,
  frequency: 5,
  deviationPercent: 50,
  type: 'spike',
};

interface Props {
  property: PropertySpec;
  onChange: (updated: PropertySpec) => void;
}

export default function PropertySpecRow({ property, onChange }: Props) {
  const [lookupInput, setLookupInput] = useState('');
  const [showAnomaly, setShowAnomaly] = useState(false);
  // Track whether we've already auto-loaded for this property so we don't
  // re-fetch every render cycle.
  const autoLoadedRef = useRef(false);

  const update = (patch: Partial<PropertySpec>) =>
    onChange({ ...property, ...patch });

  const updateConstraint = (patch: Partial<PropertySpec['constraints']>) =>
    onChange({ ...property, constraints: { ...property.constraints, ...patch } });

  const updateAnomaly = (patch: Partial<AnomalyConfig>) => {
    const current = property.constraints.anomaly ?? { ...DEFAULT_ANOMALY };
    updateConstraint({ anomaly: { ...current, ...patch } });
  };

  const handleAddLookup = () => {
    const v = lookupInput.trim();
    if (!v) return;
    const existing = property.constraints.lookupValues ?? [];
    if (!existing.includes(v)) {
      updateConstraint({ lookupValues: [...existing, v] });
    }
    setLookupInput('');
  };

  const handleRemoveLookup = (val: string) => {
    updateConstraint({
      lookupValues: (property.constraints.lookupValues ?? []).filter(
        (v) => v !== val
      ),
    });
  };

  // Auto-load lookup values from the sample JSON the first time this
  // property is set to categorical and has no existing values.
  useEffect(() => {
    if (
      property.dataType !== 'categorical' ||
      (property.constraints.lookupValues ?? []).length > 0 ||
      autoLoadedRef.current
    ) {
      return;
    }
    autoLoadedRef.current = true;
    fetch('/config/simulation-lookup-samples.json')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<Record<string, string>>;
      })
      .then((data) => {
        updateConstraint({ lookupValues: Object.values(data).map(String) });
      })
      // silently ignore fetch errors — user can add values manually
      .catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.dataType]);

  const renderConstraints = () => {
    const dt = property.dataType;

    if (dt === 'numerical_continuous' || dt === 'numerical_discrete') {
      return (
        <div className="ds-constraint-group">
          <label>
            <InfoTip text="Minimum value the simulator will generate.">Min</InfoTip>
            <input
              type="number"
              value={property.constraints.min ?? ''}
              onChange={(e) =>
                updateConstraint({
                  min: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
          </label>
          <label>
            <InfoTip text="Maximum value the simulator will generate.">Max</InfoTip>
            <input
              type="number"
              value={property.constraints.max ?? ''}
              onChange={(e) =>
                updateConstraint({
                  max: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
          </label>
          {dt === 'numerical_discrete' && (
            <label>
              <InfoTip text="Increment step for discrete values.">Step</InfoTip>
              <input
                type="number"
                value={property.constraints.step ?? ''}
                onChange={(e) =>
                  updateConstraint({
                    step: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
              />
            </label>
          )}
          <label>
            <InfoTip text="Sampling distribution: Uniform, Normal (bell curve), Log-Normal, or None (constant).">Distribution</InfoTip>
            <select
              value={property.constraints.distribution ?? 'uniform'}
              onChange={(e) =>
                updateConstraint({
                  distribution: e.target.value as PropertySpec['constraints']['distribution'],
                })
              }
            >
              <option value="uniform">Uniform</option>
              <option value="normal">Normal</option>
              <option value="log_normal">Log-Normal</option>
              <option value="none">None</option>
            </select>
          </label>
        </div>
      );
    }

    if (dt === 'categorical') {
      return (
        <div className="ds-constraint-group">
          <div className="ds-lookup-editor">
            <label>
              <InfoTip text="Allowed categorical values. Simulator picks one at random.">Lookup Values</InfoTip>
            </label>
            <div className="ds-lookup-tags">
              {(property.constraints.lookupValues ?? []).map((v) => (
                <span key={v} className="ds-tag">
                  {v}
                  <button
                    className="ds-tag-remove"
                    onClick={() => handleRemoveLookup(v)}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <div className="ds-lookup-add">
              <input
                type="text"
                placeholder="Add value…"
                value={lookupInput}
                onChange={(e) => setLookupInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddLookup()}
              />
              <button onClick={handleAddLookup}>+</button>
            </div>
          </div>
        </div>
      );
    }

    if (dt === 'datetime') {
      return (
        <div className="ds-constraint-group">
          <label>
            <InfoTip text="Earliest date/time the simulator will generate.">Min Date</InfoTip>
            <input
              type="date"
              value={property.constraints.minDate ?? ''}
              onChange={(e) => updateConstraint({ minDate: e.target.value })}
            />
          </label>
          <label>
            <InfoTip text="Latest date/time the simulator will generate.">Max Date</InfoTip>
            <input
              type="date"
              value={property.constraints.maxDate ?? ''}
              onChange={(e) => updateConstraint({ maxDate: e.target.value })}
            />
          </label>
        </div>
      );
    }

    if (dt === 'text') {
      return (
        <div className="ds-constraint-group">
          <label>
            <InfoTip text="Maximum character length of the generated text string.">Max Length</InfoTip>
            <input
              type="number"
              value={property.constraints.maxLength ?? ''}
              onChange={(e) =>
                updateConstraint({
                  maxLength:
                    e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
          </label>
          <label>
            <InfoTip text="Optional regex pattern hint (informational only).">Pattern</InfoTip>
            <input
              type="text"
              value={property.constraints.pattern ?? ''}
              onChange={(e) => updateConstraint({ pattern: e.target.value })}
            />
          </label>
        </div>
      );
    }

    if (dt === 'boolean') {
      return (
        <div className="ds-constraint-group">
          <span className="ds-small-note">Boolean – true / false</span>
        </div>
      );
    }

    return null;
  };

  return (
    <tr className={property.included ? '' : 'ds-row-excluded'}>
      <td>
        <input
          type="checkbox"
          checked={property.included}
          onChange={(e) => update({ included: e.target.checked })}
        />
      </td>

      <td className="ds-cell-name">
        <strong>{property.displayName}</strong>
        {property.displayName !== property.name && (
          <span className="ds-sub-name">{property.name}</span>
        )}
      </td>

      <td>
        <span className="ds-badge ds-badge-type">{property.dtdlContentType}</span>
      </td>

      <td>
        <code>{property.dtdlSchema}</code>
      </td>

      <td className="ds-cell-value">
        {property.twinValue !== null && property.twinValue !== undefined
          ? String(property.twinValue)
          : <span className="ds-null-val">null</span>}
      </td>

      <td>
        <select
          value={property.dataType}
          onChange={(e) => update({ dataType: e.target.value as DataTypeClass })}
        >
          {DATA_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </td>

      <td className="ds-cell-constraints">{renderConstraints()}</td>

      <td>
        <input
          type="checkbox"
          checked={property.constraints.nullable ?? false}
          onChange={(e) => updateConstraint({ nullable: e.target.checked })}
        />
      </td>

      <td className="ds-cell-anomaly">
        <button
          className="ds-btn-anomaly-toggle"
          onClick={() => setShowAnomaly(!showAnomaly)}
        >
          {showAnomaly ? '▾' : '▸'} Anomaly
        </button>
        {showAnomaly && (
          <div className="ds-anomaly-config">
            <label className="ds-anomaly-enable">
              <input
                type="checkbox"
                checked={property.constraints.anomaly?.enabled ?? false}
                onChange={(e) => updateAnomaly({ enabled: e.target.checked })}
              />
              <InfoTip text="Enable anomaly injection for this property.">Enable</InfoTip>
            </label>
            {property.constraints.anomaly?.enabled && (
              <>
                <label>
                  <InfoTip text="Percentage of samples that will be anomalous (0–100).">Freq %</InfoTip>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={property.constraints.anomaly?.frequency ?? 5}
                    onChange={(e) => updateAnomaly({ frequency: Number(e.target.value) })}
                  />
                </label>
                <label>
                  <InfoTip text="Deviation magnitude as a percentage of the value range (1–500).">Dev %</InfoTip>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={property.constraints.anomaly?.deviationPercent ?? 50}
                    onChange={(e) =>
                      updateAnomaly({ deviationPercent: Number(e.target.value) })
                    }
                  />
                </label>
                <label>
                  <InfoTip text="Anomaly type: Spike, Drift, Stuck, or Noise.">Type</InfoTip>
                  <select
                    value={property.constraints.anomaly?.type ?? 'spike'}
                    onChange={(e) =>
                      updateAnomaly({ type: e.target.value as AnomalyType })
                    }
                  >
                    {ANOMALY_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
